import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AmplitudeService, InyovaConfigService as InyovaSharedService } from '@inyova/inyova-shared';
import { CustomerAccount, Customer, CustomerAccountItem, transformAccount, Promotions } from '@inyova/models';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash-es';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, select } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import * as AppRootActions from '@app/app.actions';
import * as ProfileActions from '@app/modules/profile/profile.actions';
import * as StrategyActions from '@app/modules/strategy/strategy.actions';
import * as fromAppRoot from '@app/app.reducers';

import { PromoCampaign } from '@shared/models/DigitalPromo';
import { ABTest, CreatedPromotions, CreatedPromotionsError, ReferralRewards, VerificationCode } from '@shared/models/Profile';
import { ThirdPartyServiceItem } from '@shared/models/ThirdPartService';
import { AccountService } from '@shared/services/account.service';
import { SharedService } from '@shared/services/shared.service';
import { ToastNotificationService } from '@shared/services/toast-notification.service';
import { TrackingService } from '@shared/services/tracking.service';

@Injectable()
export class AppRootEffects {
  fetchReminder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppRootActions.fetchReminder),
      mergeMap(() =>
        this.sharedService.getIdNowReminder().pipe(
          map((response: { data: { attributes: { remind_at } } }) =>
            AppRootActions.fetchReminderSuccess({ reminder: response.data.attributes.remind_at })
          ),
          catchError(() => of(AppRootActions.fetchReminderFail()))
        )
      )
    )
  );

  updateReminder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppRootActions.updateReminder),
      mergeMap(({ reminder }) => {
        return this.sharedService.setIdNowReminder(reminder).pipe(
          map((response: { data: { attributes: { remind_at } } }) =>
            AppRootActions.updateReminderSuccess({ reminder: response.data.attributes.remind_at })
          ),
          catchError(() => of(AppRootActions.updateReminderFail()))
        );
      })
    );
  });

  fetchCustomer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppRootActions.fetchCustomer),
      mergeMap(() => {
        return this.accountService.fetchCustomer().pipe(
          map((customer: Customer) => AppRootActions.fetchCustomerSuccess({ customer, selectActiveAccount: true })),
          catchError(() => of(AppRootActions.fetchCustomerFail()))
        );
      })
    );
  });

  fetchCustomerSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppRootActions.fetchCustomerSuccess),
      filter(({ customer, selectActiveAccount }) => selectActiveAccount && customer.temporary_accounts && customer.temporary_accounts.length > 0),
      map(({ customer }) => {
        let defaultAccount: CustomerAccountItem;
        const currentAccountID = localStorage.getItem('currentAccountID');

        if (customer.temporary_accounts.length === 1) {
          defaultAccount = { ...customer.temporary_accounts[0] };
        } else {
          if (currentAccountID) {
            defaultAccount = customer.temporary_accounts.find((account: CustomerAccountItem) => account.id === currentAccountID);
          }

          if (!defaultAccount || !defaultAccount.id) {
            // we used opposite order because reduce method finds the last item in the array.
            // The array will be ordered like ['kid','kid','3a','3b']
            const accountOrder = ['kid', '3a', '3b', 'grow'];
            const orderedAccounts = [...customer.temporary_accounts];
            orderedAccounts.sort((a, b) => accountOrder.indexOf(a.kind) - accountOrder.indexOf(b.kind));

            // find step 5 last account according to the this rule 3b > 3a > kid
            defaultAccount = orderedAccounts.reduce((firstAccount: CustomerAccountItem, secondAccount: CustomerAccountItem) =>
              firstAccount.step_status > secondAccount.step_status ? firstAccount : secondAccount
            );
          }
        }

        return AppRootActions.fetchCustomerSelectActiveAccount({
          customerAccountDetail: { ...fromAppRoot.initialState.currentAccountDetail, ...defaultAccount } as CustomerAccount
        });
      })
    )
  );

  resendVerificationEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppRootActions.resendVerificationEmail),
      switchMap(({ customerId }) =>
        this.accountService.resendVerificationEmail().pipe(
          map(() => AppRootActions.resendVerificationEmailSuccess(Date.now() + 60000, customerId)),
          catchError((err) => of(AppRootActions.resendVerificationEmailFail(err)))
        )
      )
    )
  );

  cooldownAfterResendVerificationEmail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppRootActions.resendVerificationEmailSuccess),
        tap(({ cooldownUntil, customerId }: { cooldownUntil: number; customerId: number }) => {
          this.amplitudeService.trackEvent('Email Confirmation Sent', { send_reason: 'manual_resend' });
          localStorage.setItem(`resendEmailCooldown_${customerId}`, cooldownUntil.toString());
        })
      ),
    {
      dispatch: false
    }
  );

  loadResendEmailCooldown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppRootActions.loadResendEmailCooldown),
      concatLatestFrom(() => this.store.select(fromAppRoot.selectCustomer)),
      map(([, { id }]) => localStorage.getItem(`resendEmailCooldown_${id}`)),
      filter((cooldownUntil: string | null) => cooldownUntil !== null),
      map((cooldownUntil) => AppRootActions.loadResendEmailCooldownSuccess(Number(cooldownUntil)))
    )
  );

  fetchCustomerSelectActiveAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppRootActions.fetchCustomerSelectActiveAccount),
      map(({ customerAccountDetail }) => AppRootActions.fetchCustomerAccountDetail({ accountID: customerAccountDetail.id }))
    )
  );

  fetchCustomerAccountDetail$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppRootActions.fetchCustomerAccountDetail),
      mergeMap(({ accountID }) => {
        return this.accountService.fetchCustomerAccountDetail(accountID).pipe(
          tap((apiAccount) => {
            // Add latest user ID to localstorage
            localStorage.setItem('currentAccountID', apiAccount.id);
          }),
          map((apiAccount) => AppRootActions.fetchCustomerAccountDetailSuccess({ customerAccountDetail: transformAccount(apiAccount) })),
          catchError(() => of(AppRootActions.fetchCustomerAccountDetailFail()))
        );
      })
    );
  });

  fetchCustomerAccountDetailSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AppRootActions.fetchCustomerAccountDetailSuccess),
        tap(({ customerAccountDetail }) => {
          this.inyovaSharedService.setAccountId(customerAccountDetail.id);
        })
      );
    },
    { dispatch: false }
  );

  createNewAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppRootActions.createNewAccount),
      concatLatestFrom(() => [this.store.pipe(select(fromAppRoot.selectCustomer))]),
      exhaustMap(([{ kind, owner_name }, customer]) => {
        return this.accountService.createAccount({ kind, owner_name }).pipe(
          mergeMap((apiAccount) => {
            const newAccount = {
              id: apiAccount.id,
              kind: kind as '3b' | '3a' | 'kid' | 'grow',
              owner_name: owner_name,
              step_status: 1,
              account_currency: apiAccount.currency,
              enabled_features: []
            };

            return [
              AppRootActions.toggleNewAccountIsLoading({ isLoading: false }),
              AppRootActions.setSwitchAccountLoading({
                show: true,
                message: this.translateService.instant(`fullLoader.switchingAccountType.${kind === 'kid' ? 'kidsAccount' : kind}`)
              }),
              AppRootActions.fetchCustomerSuccess({
                customer: {
                  ...customer,
                  accounts: [...customer.accounts, newAccount],
                  temporary_accounts: [...customer.temporary_accounts, newAccount]
                },
                selectActiveAccount: false
              }),
              AppRootActions.switchAccount({ id: apiAccount.id, redirectUrl: null })
            ];
          }),
          catchError(() => of(AppRootActions.createNewAccountFail()))
        );
      })
    );
  });

  switchAccount$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppRootActions.switchAccount),
      switchMap(({ id, redirectUrl }) => {
        return this.accountService.fetchCustomerAccountDetail(id).pipe(
          tap((apiAccount) => {
            localStorage.removeItem('CIAccount');
            // Add latest user ID to localstorage
            localStorage.setItem('currentAccountID', apiAccount.id);
            setTimeout(() => {
              this.store.dispatch(AppRootActions.setSwitchAccountLoading({ show: false, message: '' }));
            }, 800);

            if (apiAccount.step_status === 1) {
              this.store.dispatch(StrategyActions.setTabIndex({ tabIndex: apiAccount.ql_onboarding_step }));
            }

            // autoRoute method handle fetch no need to dispatch strategy
            void this.router.navigateByUrl(redirectUrl ?? '/');
          }),
          mergeMap((apiAccount) => [
            AppRootActions.toggleCIAccountActive({ show: false }),
            AppRootActions.fetchCustomerAccountDetailSuccess({ customerAccountDetail: transformAccount(apiAccount) })
          ]),
          catchError(() => of(AppRootActions.setSwitchAccountLoading({ show: false, message: '' })))
        );
      })
    );
  });

  sendVerificationCode$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppRootActions.sendVerificationCode),
      mergeMap(() => {
        return this.sharedService.sendVerificationCode().pipe(
          map((response: VerificationCode) => AppRootActions.sendVerificationCodeSuccess({ status: response.status, date: new Date() })),
          catchError(() => of(AppRootActions.sendVerificationCodeFail()))
        );
      })
    );
  });

  verifyVerificationCode$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppRootActions.verifyVerificationCode),
      mergeMap(({ code }) => {
        return this.sharedService.verifyVerificationCode(code).pipe(
          map((response: VerificationCode) => AppRootActions.verifyVerificationCodeSuccess({ status: response.status, date: new Date() })),
          catchError(() => of(AppRootActions.verifyVerificationCodeFail()))
        );
      })
    );
  });

  referralsPageOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppRootActions.referralsPageOpened, AppRootActions.getPromotions),
      concatLatestFrom(() => this.store.select(fromAppRoot.selectPromotionsLoadState)),
      filter(([, loadState]) => loadState !== 'LOADED'),
      switchMap(() =>
        this.sharedService.getPromotions().pipe(map((response) => AppRootActions.getPromotionsSuccess({ data: response as unknown as Promotions })))
      ),
      catchError(() => of(AppRootActions.getPromotionsFail()))
    )
  );

  // Fetch promotions only if they are not initialised, for immediate fetch use fetchPromotionsForced

  updatePromotions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppRootActions.updatePromotions),
      mergeMap(({ payload }) => {
        return this.sharedService.postPromotions(payload).pipe(
          map((response: CreatedPromotions) => AppRootActions.updatePromotionsSuccess({ response })),
          catchError((response: { error: CreatedPromotionsError[] }) => of(AppRootActions.updatePromotionsFail({ response: response.error })))
        );
      })
    );
  });

  getPromotionsAfterUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppRootActions.updatePromotionsSuccess),
      map(() => AppRootActions.getPromotions(true))
    )
  );

  fetchThirdParties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppRootActions.fetchThirdParties),
      mergeMap(({ service }) =>
        this.sharedService.getThirdPartyServices(service).pipe(
          map((response: { data: ThirdPartyServiceItem[] }) => AppRootActions.fetchThirdPartiesSuccess({ response: response.data })),
          catchError(() => of(AppRootActions.fetchThirdPartiesFail()))
        )
      )
    )
  );

  fetchReferralRewards$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AppRootActions.fetchReferralRewards),
      mergeMap(({ referralCode }) => {
        return this.sharedService.getReferralRewards(referralCode).pipe(
          map((response: { data: { attributes: ReferralRewards } }) => {
            document.cookie = `referral=${referralCode}`;
            return AppRootActions.fetchReferralRewardsSuccess({ response: response.data.attributes });
          }),
          catchError(() => of(AppRootActions.fetchReferralRewardsFail()))
        );
      })
    );
  });

  fetchABTests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppRootActions.fetchABTests),
      mergeMap(() =>
        this.sharedService.getABTestList().pipe(
          map((res: { data: ABTest[]; meta: [] }) =>
            AppRootActions.fetchABTestsSuccess({
              data: res.data
            })
          ),
          catchError(() => of(AppRootActions.fetchABTestsFail()))
        )
      )
    )
  );

  updateSpDeposit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppRootActions.updateSpDeposit),
      mergeMap(({ deposit }) =>
        this.accountService.updateSavingsPlan(deposit).pipe(
          mergeMap(() => [AppRootActions.updateSpDepositSuccess(), AppRootActions.toggleDepositSuccessDialog({ show: true })]),
          catchError(() => of(AppRootActions.updateSpDepositFail()))
        )
      )
    )
  );

  getPromoCampaigns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppRootActions.getPromoCampaigns),
      mergeMap(() =>
        this.accountService.getPromoCampaigns().pipe(
          map((response) => AppRootActions.getPromoCampaignsSuccess({ data: addPromosToCampaigns(response) })),
          catchError(() => of(AppRootActions.getPromoCampaignsFail()))
        )
      )
    )
  );

  setCampaignParticipation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppRootActions.setCampaignParticipation),
      concatLatestFrom(() => [this.store.pipe(select(fromAppRoot.selectPromoCampaigns))]),
      mergeMap(([{ participation, id }, campaigns]) =>
        this.accountService.setPromoParticipation(participation, id).pipe(
          map(() =>
            AppRootActions.setCampaignParticipationSuccess({
              data: campaigns.map((campaign) => {
                if (Number(campaign.id) === id) {
                  const updatedCampaign = cloneDeep(campaign);
                  updatedCampaign.attributes.opted_in = participation;
                  return updatedCampaign;
                }
                return campaign;
              })
            })
          ),
          catchError(() => of(AppRootActions.setCampaignParticipationFail()))
        )
      )
    )
  );

  submitGreennessSurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppRootActions.submitGreennessSurvey),
      concatLatestFrom(() => this.store.select(fromAppRoot.selectCustomer)),
      switchMap(([{ data }, customer]) => {
        return this.accountService.submitGreennessSurvey(data).pipe(
          map(() => {
            if (localStorage.getItem(`hideGreennessSurvey_${customer.id}`)) {
              localStorage.removeItem(`hideGreennessSurvey_${customer.id}`);
            }
            return AppRootActions.submitGreennessSurveySuccess({ success: true });
          }),
          catchError(() => of(AppRootActions.submitGreennessSurveyFail({ success: false })))
        );
      })
    )
  );

  submitOnboardingGreennessSurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppRootActions.submitOnboardingGreennessSurvey),
      mergeMap(({ data }) =>
        this.accountService.submitGreennessSurvey(data).pipe(
          map(() => AppRootActions.submitOnboardingGreennessSurveySuccess({ success: true })),
          tap(() => {
            this.router.navigate(['/']);
          }),
          catchError(() => of(AppRootActions.submitOnboardingGreennessSurveyFail({ success: false })))
        )
      )
    )
  );

  showToastGeneralError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ...[
            AppRootActions.updateReminderFail,
            AppRootActions.createNewAccountFail,
            AppRootActions.sendVerificationCodeFail,
            AppRootActions.verifyVerificationCodeFail,
            AppRootActions.updateSpDepositFail,
            AppRootActions.setCampaignParticipationFail
          ]
        ),
        tap(() => {
          this.toastNotificationService.show('critical', this.translateService.instant('personal.flash.error_rev') as string);
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProfileActions.logout),
        tap(() => this.inyovaSharedService.setAccountId(null))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private router: Router,
    private store: Store,
    private sharedService: SharedService,
    private accountService: AccountService,
    private trackingService: TrackingService,
    private toastNotificationService: ToastNotificationService,
    private translateService: TranslateService,
    private inyovaSharedService: InyovaSharedService,
    private amplitudeService: AmplitudeService
  ) {}
}

function addPromosToCampaigns(response: PromoCampaign): PromoCampaign {
  const campaigns = response.data.map((campaign) => {
    const promoIDs = campaign.relationships.deposit_promotions.data.map((promo) => promo.id);
    return {
      ...campaign,
      promotions: response.included
        .filter((promotion) => {
          return promoIDs.includes(promotion.id);
        })
        .sort((a, b) => a.attributes.value - b.attributes.value)
    };
  });
  return { ...response, data: campaigns };
}
